import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { PhoneIcon, MailIcon, MapPinIcon, MessageCircleIcon, SendIcon, ClockIcon, UserIcon, CheckIcon, ArrowRightIcon, MessageSquareIcon } from 'lucide-react';
interface ContactPageProps {
  onNavigate: (page: string) => void;
}
function ContactHero({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  return <section className="relative w-full min-h-[45vh] flex items-end overflow-hidden" data-id="element-767">
      <div className="absolute inset-0" data-id="element-768">
        <img src="https://images.unsplash.com/photo-1558799401-1dcba79834c2?w=1920&q=80" alt="Kathmandu cityscape with temples" className="w-full h-full object-cover" data-id="element-769" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" data-id="element-770" />
      </div>
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14 pt-32" data-id="element-771">
        <nav className="mb-5" aria-label="Breadcrumb" data-id="element-772">
          <ol className="flex items-center gap-2 text-sm text-white/60" data-id="element-773">
            <li data-id="element-774">
              <button onClick={() => onNavigate('home')} className="hover:text-white transition-colors" data-id="element-775">
                Home
              </button>
            </li>
            <li data-id="element-776">/</li>
            <li className="text-[#D4A853]" data-id="element-777">Contact Us</li>
          </ol>
        </nav>
        <motion.h1 initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4" data-id="element-778">
          Get in{' '}
          <span className="italic font-medium text-[#D4A853]" data-id="element-779">Touch</span>
        </motion.h1>
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.15
      }} className="text-white/70 text-lg max-w-2xl font-light" data-id="element-780">
          Reach out to us anytime — we're here to help
        </motion.p>
      </div>
    </section>;
}
function QuickContact() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <section className="w-full bg-[#FAF8F5] py-16 sm:py-20" ref={ref} data-id="element-781">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-782">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-783">
          <motion.a href="https://wa.me/9779851032316" target="_blank" rel="noopener noreferrer" initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0
        }} className="flex items-center gap-4 bg-white rounded-xl p-5 border border-[#D4A853]/10 hover:shadow-md transition-shadow" data-id="element-784">
            <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-785">
              <MessageCircleIcon className="w-6 h-6 text-green-600" data-id="element-786" />
            </div>
            <div data-id="element-787">
              <p className="text-slate-900 text-sm font-semibold" data-id="element-788">WhatsApp</p>
              <p className="text-slate-500 text-xs mt-0.5" data-id="element-789">
                Chat with us instantly
              </p>
            </div>
          </motion.a>

          <motion.a href="tel:+9779851032316" initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.08
        }} className="flex items-center gap-4 bg-white rounded-xl p-5 border border-[#D4A853]/10 hover:shadow-md transition-shadow" data-id="element-790">
            <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-791">
              <PhoneIcon className="w-6 h-6 text-teal-600" data-id="element-792" />
            </div>
            <div data-id="element-793">
              <p className="text-slate-900 text-sm font-semibold" data-id="element-794">Call Us</p>
              <p className="text-slate-500 text-xs mt-0.5" data-id="element-795">+977 9851032316</p>
            </div>
          </motion.a>

          <motion.a href="mailto:info@nepalluxurytravel.com" initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.16
        }} className="flex items-center gap-4 bg-white rounded-xl p-5 border border-[#D4A853]/10 hover:shadow-md transition-shadow" data-id="element-796">
            <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-797">
              <MailIcon className="w-6 h-6 text-blue-600" data-id="element-798" />
            </div>
            <div data-id="element-799">
              <p className="text-slate-900 text-sm font-semibold" data-id="element-800">Email</p>
              <p className="text-slate-500 text-xs mt-0.5" data-id="element-801">
                info@nepalluxurytravel.com
              </p>
            </div>
          </motion.a>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.24
        }} className="flex items-center gap-4 bg-white rounded-xl p-5 border border-[#D4A853]/10" data-id="element-802">
            <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-803">
              <ClockIcon className="w-6 h-6 text-amber-600" data-id="element-804" />
            </div>
            <div data-id="element-805">
              <p className="text-slate-900 text-sm font-semibold" data-id="element-806">
                Office Hours
              </p>
              <p className="text-slate-500 text-xs mt-0.5" data-id="element-807">
                Sun–Fri, 9 AM – 6 PM NPT
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
function ContactFormSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  const [submitted, setSubmitted] = useState(false);
  return <section className="w-full bg-white py-20 sm:py-28" ref={ref} data-id="element-808">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-809">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16" data-id="element-810">
          {/* Contact Info Card */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7
        }} data-id="element-811">
            <div className="bg-[#0C1F2C] rounded-2xl p-8 sm:p-10 border border-[#D4A853]/15 h-full" data-id="element-812">
              <h3 className="font-playfair text-2xl font-semibold text-white mb-2" data-id="element-813">
                Contact Information
              </h3>
              <p className="text-slate-400 text-base mb-8" data-id="element-814">
                We'd love to hear from you. Reach out through any of these
                channels.
              </p>

              <div className="space-y-6" data-id="element-815">
                <div className="flex items-start gap-4" data-id="element-816">
                  <div className="w-10 h-10 bg-[#D4A853]/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-817">
                    <MapPinIcon className="w-5 h-5 text-[#D4A853]" data-id="element-818" />
                  </div>
                  <div data-id="element-819">
                    <p className="text-white text-sm font-medium" data-id="element-820">
                      Office Address
                    </p>
                    <p className="text-slate-400 text-sm mt-0.5" data-id="element-821">
                      Thamel, Kathmandu, Nepal 44600
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-822">
                  <div className="w-10 h-10 bg-[#D4A853]/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-823">
                    <PhoneIcon className="w-5 h-5 text-[#D4A853]" data-id="element-824" />
                  </div>
                  <div data-id="element-825">
                    <p className="text-white text-sm font-medium" data-id="element-826">Phone</p>
                    <a href="tel:+9779851032316" className="text-slate-400 text-sm hover:text-teal-400 transition-colors" data-id="element-827">
                      +977 9851032316
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-828">
                  <div className="w-10 h-10 bg-[#D4A853]/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-829">
                    <MailIcon className="w-5 h-5 text-[#D4A853]" data-id="element-830" />
                  </div>
                  <div data-id="element-831">
                    <p className="text-white text-sm font-medium" data-id="element-832">Email</p>
                    <a href="mailto:info@nepalluxurytravel.com" className="text-slate-400 text-sm hover:text-teal-400 transition-colors" data-id="element-833">
                      info@nepalluxurytravel.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-834">
                  <div className="w-10 h-10 bg-[#D4A853]/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-835">
                    <MessageCircleIcon className="w-5 h-5 text-[#D4A853]" data-id="element-836" />
                  </div>
                  <div data-id="element-837">
                    <p className="text-white text-sm font-medium" data-id="element-838">WhatsApp</p>
                    <a href="https://wa.me/9779851032316" target="_blank" rel="noopener noreferrer" className="text-slate-400 text-sm hover:text-teal-400 transition-colors" data-id="element-839">
                      Chat on WhatsApp
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-840">
                  <div className="w-10 h-10 bg-[#D4A853]/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-841">
                    <ClockIcon className="w-5 h-5 text-[#D4A853]" data-id="element-842" />
                  </div>
                  <div data-id="element-843">
                    <p className="text-white text-sm font-medium" data-id="element-844">
                      Office Hours
                    </p>
                    <p className="text-slate-400 text-sm mt-0.5" data-id="element-845">
                      Sunday – Friday, 9:00 AM – 6:00 PM NPT
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* General Inquiry Form */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7,
          delay: 0.15
        }} data-id="element-846">
            <div className="flex items-center gap-3 mb-6" data-id="element-847">
              <span className="text-[#D4A853]/40 text-xs" data-id="element-848">◆</span>
              <div className="w-12 h-0.5 bg-[#D4A853]" data-id="element-849" />
              <span className="text-[#D4A853]/40 text-xs" data-id="element-850">◆</span>
            </div>
            <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-3" data-id="element-851">
              Send Us a Message
            </h2>
            <p className="text-slate-500 font-light text-lg mb-8" data-id="element-852">
              Have a question or feedback? We'll get back to you within 24
              hours.
            </p>

            {submitted ? <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="text-center py-16 bg-[#FAF8F5] rounded-2xl border border-[#D4A853]/10" data-id="element-853">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-854">
                  <CheckIcon className="w-8 h-8 text-teal-600" data-id="element-855" />
                </div>
                <h3 className="font-playfair text-2xl font-bold text-slate-900 mb-2" data-id="element-856">
                  Message Sent!
                </h3>
                <p className="text-slate-500 text-sm" data-id="element-857">
                  We'll get back to you within 24 hours.
                </p>
              </motion.div> : <form onSubmit={e => {
            e.preventDefault();
            setSubmitted(true);
            setTimeout(() => setSubmitted(false), 4000);
          }} className="space-y-5" data-id="element-858">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-859">
                  <div data-id="element-860">
                    <label htmlFor="cq-name" className="block text-sm text-slate-700 font-medium mb-1.5" data-id="element-861">
                      Full Name
                    </label>
                    <div className="relative" data-id="element-862">
                      <UserIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-863" />
                      <input id="cq-name" type="text" required placeholder="Your full name" className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors" data-id="element-864" />
                    </div>
                  </div>
                  <div data-id="element-865">
                    <label htmlFor="cq-email" className="block text-sm text-slate-700 font-medium mb-1.5" data-id="element-866">
                      Email Address
                    </label>
                    <div className="relative" data-id="element-867">
                      <MailIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-868" />
                      <input id="cq-email" type="email" required placeholder="you@example.com" className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors" data-id="element-869" />
                    </div>
                  </div>
                </div>

                <div data-id="element-870">
                  <label htmlFor="cq-subject" className="block text-sm text-slate-700 font-medium mb-1.5" data-id="element-871">
                    Subject
                  </label>
                  <select id="cq-subject" className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors appearance-none" data-id="element-872">
                    <option data-id="element-873">General Inquiry</option>
                    <option data-id="element-874">Partnership Opportunity</option>
                    <option data-id="element-875">Feedback</option>
                    <option data-id="element-876">Media & Press</option>
                    <option data-id="element-877">Careers</option>
                    <option data-id="element-878">Other</option>
                  </select>
                </div>

                <div data-id="element-879">
                  <label htmlFor="cq-message" className="block text-sm text-slate-700 font-medium mb-1.5" data-id="element-880">
                    Your Message
                  </label>
                  <div className="relative" data-id="element-881">
                    <MessageSquareIcon className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-400" data-id="element-882" />
                    <textarea id="cq-message" rows={5} required placeholder="How can we help you?" className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors resize-none" data-id="element-883" />
                  </div>
                </div>

                <button type="submit" className="inline-flex items-center gap-2 px-8 py-3.5 bg-teal-600 text-white font-medium rounded-full hover:bg-teal-700 transition-all duration-300 shadow-lg shadow-teal-600/20 text-sm tracking-wide" data-id="element-884">
                  <SendIcon className="w-4 h-4" data-id="element-885" />
                  Send Message
                </button>
              </form>}
          </motion.div>
        </div>
      </div>
    </section>;
}
function PlanTripCTA({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <section className="w-full bg-[#FAF8F5] py-16 sm:py-20" ref={ref} data-id="element-886">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-887">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} className="bg-white rounded-2xl p-8 sm:p-12 border border-[#D4A853]/10 text-center" data-id="element-888">
          <h3 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 mb-3" data-id="element-889">
            Looking to Plan a Trip?
          </h3>
          <p className="text-slate-500 font-light text-lg max-w-lg mx-auto mb-8" data-id="element-890">
            Tell us about your dream Nepal adventure and our travel experts will
            craft a personalized itinerary just for you.
          </p>
          <button onClick={() => onNavigate('plan-trip')} className="inline-flex items-center gap-2 px-8 py-3.5 bg-teal-600 text-white font-medium rounded-full hover:bg-teal-700 transition-all duration-300 shadow-lg shadow-teal-600/20 text-sm tracking-wide" data-id="element-891">
            Plan Your Trip
            <ArrowRightIcon className="w-4 h-4" data-id="element-892" />
          </button>
        </motion.div>
      </div>
    </section>;
}
export function ContactPage({
  onNavigate
}: ContactPageProps) {
  return <div className="w-full" data-id="element-893">
      <ContactHero onNavigate={onNavigate} data-id="element-894" />
      <QuickContact data-id="element-895" />
      <ContactFormSection data-id="element-896" />
      <PlanTripCTA onNavigate={onNavigate} data-id="element-897" />
    </div>;
}